<template>
    <div class="config-page">
        <!-- Page header -->
        <div class="config-header">
            <h1 class="config-title">{{ $t('configuracion') }}</h1>
            <p class="config-subtitle">Administra las preferencias de tu cuenta y empresa</p>
        </div>

        <!-- "At least one day" dialog -->
        <v-dialog v-model="dialog_at_least_one_day" max-width="320">
            <v-card>
                <v-card-title class="headline">{{ $t('aviso') }}</v-card-title>
                <v-card-text>{{ $t('msg_no_days2') }}</v-card-text>
                <v-card-actions>
                    <v-spacer></v-spacer>
                    <v-btn color="primary" flat @click="dialog_at_least_one_day = false">{{ $t('aceptar') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

        <v-container fluid class="pa-4">
            <v-layout row wrap>

                <!-- ── Change Password ── -->
                <v-flex xs12 sm6 md4 class="pa-2">
                    <v-card class="config-card">
                        <div class="config-card-header">
                            <v-icon class="config-card-icon">lock</v-icon>
                            <span>{{ $t('change_password') }}</span>
                        </div>
                        <v-card-text class="pt-3">
                            <v-form @submit.prevent="change_password()">
                                <v-text-field
                                    v-model="current_password"
                                    :label="$t('current_password')"
                                    prepend-inner-icon="lock_outline"
                                    :type="show ? 'text' : 'password'"
                                    :append-icon="show ? 'visibility' : 'visibility_off'"
                                    @click:append="show = !show"
                                    :error-messages="errores.current_password"
                                    outline
                                    dense
                                ></v-text-field>
                                <v-text-field
                                    v-model="password"
                                    :label="$t('new_password')"
                                    prepend-inner-icon="lock_outline"
                                    :type="show ? 'text' : 'password'"
                                    :append-icon="show ? 'visibility' : 'visibility_off'"
                                    @click:append="show = !show"
                                    :error-messages="errores.new_password"
                                    data-vv-name="password"
                                    ref="password"
                                    outline
                                    dense
                                ></v-text-field>
                                <v-text-field
                                    v-validate="'required|confirmed:password'"
                                    v-model="confirm_password"
                                    :label="$t('password_confirm')"
                                    prepend-inner-icon="lock_outline"
                                    :type="show ? 'text' : 'password'"
                                    :append-icon="show ? 'visibility' : 'visibility_off'"
                                    @click:append="show = !show"
                                    :error-messages="errors.collect('confirm_password')"
                                    data-vv-name="confirm_password"
                                    outline
                                    dense
                                ></v-text-field>
                                <div class="config-card-actions">
                                    <v-btn color="primary" type="submit" :loading="loadingChangePassword" depressed>
                                        {{ $t('modificar') }}
                                    </v-btn>
                                </div>
                            </v-form>
                        </v-card-text>
                    </v-card>
                </v-flex>

                <!-- ── Notifications ── -->
                <v-flex xs12 sm6 md4 class="pa-2">
                    <v-card class="config-card">
                        <div class="config-card-header">
                            <v-icon class="config-card-icon">notifications_none</v-icon>
                            <span>{{ $t('notificaciones') }}</span>
                        </div>
                        <v-card-text class="pt-3">
                            <v-switch v-model="canSMS" :label="$t('canSendSMS')" color="primary" hide-details class="mb-3"></v-switch>
                            <v-switch v-model="canEmail" :label="$t('canSendEmail')" color="primary" hide-details></v-switch>
                        </v-card-text>
                        <div class="config-card-actions">
                            <v-btn color="primary" :loading="loadingNotification" depressed @click="changeNotifications()">
                                {{ $t('guardar') }}
                            </v-btn>
                        </div>
                    </v-card>
                </v-flex>

                <!-- ── Survey ── (roll 5) -->
                <v-flex xs12 sm6 md4 class="pa-2" v-if="canSurvey">
                    <v-card class="config-card">
                        <div class="config-card-header">
                            <v-icon class="config-card-icon">assignment</v-icon>
                            <span>{{ $t('header_survey') }}</span>
                        </div>
                        <v-card-text class="pt-3">
                            <v-switch
                                v-model="health_survey"
                                :label="health_survey ? $t('Si') : 'No'"
                                color="primary"
                                hide-details
                            ></v-switch>
                        </v-card-text>
                        <div class="config-card-actions">
                            <v-btn color="primary" :loading="loadingSurvey" depressed @click="changeSurvey()">
                                {{ $t('guardar') }}
                            </v-btn>
                        </div>
                    </v-card>
                </v-flex>

                <!-- ── Tolerance week invitations ── (roll 5) -->
                <v-flex xs12 sm6 md4 class="pa-2" v-if="canSurvey">
                    <v-card class="config-card">
                        <div class="config-card-header">
                            <v-icon class="config-card-icon">date_range</v-icon>
                            <span>{{ $t('enable_tolerance_week_invitations') }}</span>
                        </div>
                        <v-card-text class="pt-3">
                            <p class="body-2 grey--text text--darken-1 mb-3">{{ $t('full_msg_enable_tolerance_week_invitations') }}</p>
                            <v-switch
                                v-model="tolerance_week_invitations"
                                :label="tolerance_week_invitations ? $t('Si') : 'No'"
                                color="primary"
                                hide-details
                            ></v-switch>
                        </v-card-text>
                        <div class="config-card-actions">
                            <v-btn color="primary" :loading="loadingTolerance" depressed @click="enableToleranceOneWeek()">
                                {{ $t('guardar') }}
                            </v-btn>
                        </div>
                    </v-card>
                </v-flex>

                <!-- ── Maximum capacity ── -->
                <v-flex xs12 sm6 md4 class="pa-2">
                    <v-card class="config-card">
                        <div class="config-card-header">
                            <v-icon class="config-card-icon">people</v-icon>
                            <span>{{ $t('maxium_capacity') }}</span>
                        </div>
                        <v-card-text class="pt-3">
                            <v-text-field
                                v-model="maxium_capacity"
                                :label="$t('maxium_capacity_examples')"
                                type="number"
                                prepend-inner-icon="group"
                                outline
                                dense
                            ></v-text-field>
                        </v-card-text>
                        <div class="config-card-actions">
                            <v-btn color="primary" :loading="loadingMaxiumCapacity" depressed @click="changeMaxiumCapacity()">
                                {{ $t('guardar') }}
                            </v-btn>
                        </div>
                    </v-card>
                </v-flex>

                <!-- ── Elevator access days ── (roll 4) -->
                <v-flex xs12 sm6 md4 class="pa-2" v-if="canDays">
                    <v-card class="config-card">
                        <div class="config-card-header">
                            <v-icon class="config-card-icon">calendar_today</v-icon>
                            <span>{{ $t('title_elevator_access_days') }}</span>
                        </div>
                        <v-card-text class="pt-3">
                            <p class="body-2 grey--text text--darken-1 mb-2">{{ $t('msg1_elevator_access_days') }}</p>
                            <v-layout row wrap>
                                <v-flex xs6><v-checkbox v-model="Lunes"    :label="$t('Lunes')"    color="primary" hide-details dense></v-checkbox></v-flex>
                                <v-flex xs6><v-checkbox v-model="Martes"   :label="$t('Martes')"   color="primary" hide-details dense></v-checkbox></v-flex>
                                <v-flex xs6><v-checkbox v-model="Miercoles":label="$t('Miercoles')" color="primary" hide-details dense></v-checkbox></v-flex>
                                <v-flex xs6><v-checkbox v-model="Jueves"   :label="$t('Jueves')"   color="primary" hide-details dense></v-checkbox></v-flex>
                                <v-flex xs6><v-checkbox v-model="Viernes"  :label="$t('Viernes')"  color="primary" hide-details dense></v-checkbox></v-flex>
                                <v-flex xs6><v-checkbox v-model="Sabado"   :label="$t('Sabado')"   color="primary" hide-details dense></v-checkbox></v-flex>
                                <v-flex xs6><v-checkbox v-model="Domingo"  :label="$t('Domingo')"  color="primary" hide-details dense></v-checkbox></v-flex>
                            </v-layout>
                            <p class="caption grey--text mt-2">{{ $t('msg2_elevator_access_days') }}</p>
                        </v-card-text>
                        <div class="config-card-actions">
                            <v-btn color="primary" :loading="loadingAccessDays" depressed @click="changeAccessDays()">
                                {{ $t('modificar') }}
                            </v-btn>
                        </div>
                    </v-card>
                </v-flex>

                <!-- ── Wallpaper ── (roll 5) -->
                <v-flex xs12 sm6 md4 class="pa-2" v-if="canSurvey">
                    <v-card class="config-card">
                        <div class="config-card-header">
                            <v-icon class="config-card-icon">wallpaper</v-icon>
                            <span>Fondo de pantalla</span>
                        </div>
                        <v-card-text class="pt-3">
                            <!-- Current wallpaper preview -->
                            <div v-if="wallpaperPreview || getProfile.wallpaper" class="wallpaper-preview-wrap mb-3">
                                <img
                                    :src="wallpaperPreview || getProfile.wallpaper"
                                    class="wallpaper-preview-img"
                                    alt="Fondo de pantalla actual"
                                />
                                <div class="wallpaper-preview-label caption grey--text mt-1">
                                    {{ wallpaperPreview ? 'Vista previa' : 'Fondo actual' }}
                                </div>
                            </div>
                            <div v-else class="wallpaper-empty mb-3">
                                <v-icon size="48" color="grey lighten-2">image</v-icon>
                                <div class="caption grey--text mt-1">Sin fondo configurado</div>
                            </div>

                            <!-- File input -->
                            <v-btn flat color="primary" class="pl-0" @click="$refs.wallpaperInput.click()">
                                <v-icon left>attach_file</v-icon>
                                {{ wallpaperFile ? wallpaperFile.name : 'Seleccionar imagen' }}
                            </v-btn>
                            <input
                                ref="wallpaperInput"
                                type="file"
                                accept="image/*"
                                style="display: none;"
                                @change="onWallpaperSelected"
                            />
                            <div v-if="wallpaperError" class="caption error--text mt-1">{{ wallpaperError }}</div>
                        </v-card-text>
                        <div class="config-card-actions">
                            <v-btn
                                color="primary"
                                depressed
                                :loading="loadingWallpaper"
                                :disabled="!wallpaperFile && !wallpaperCroppedDataURL"
                                @click="wallpaperFile && !wallpaperCroppedDataURL ? (dialogWallpaperCrop = true, wallpaperCropURL = wallpaperPreview) : uploadWallpaper()"
                            >
                                {{ wallpaperFile && !wallpaperCroppedDataURL ? 'Recortar imagen' : 'Guardar fondo' }}
                            </v-btn>
                            <v-btn
                                v-if="wallpaperCroppedDataURL"
                                color="warning"
                                depressed
                                text
                                @click="wallpaperFile = null; wallpaperPreview = null; wallpaperCroppedDataURL = null; wallpaperCropDegrees = 0; wallpaperCropRatio = 1.7777777777777777"
                            >
                                Cancelar
                            </v-btn>
                        </div>
                    </v-card>
                </v-flex>

                <!-- ── Wallpaper Crop Dialog ── -->
                <v-dialog v-model="dialogWallpaperCrop" persistent scrollable max-width="700px">
                    <v-card>
                        <v-toolbar dark color="primary">
                            <v-toolbar-title>
                                Recortar fondo de pantalla
                            </v-toolbar-title>
                        </v-toolbar>
                        <v-card-text>
                            <clipper-fixed
                                ref="wallpaperClipper"
                                :rotate="wallpaperCropDegrees"
                                :ratio="wallpaperCropRatio"
                                :src="wallpaperCropURL">
                            </clipper-fixed>
                        </v-card-text>
                        <v-card-actions class="justify-center">
                            <v-layout row wrap class="justify-center">
                                <v-flex xs12>
                                    <v-radio-group
                                        class="justify-center"
                                        v-model="wallpaperCropRatio"
                                        name="ratio"
                                        row
                                        label="Ratio"
                                        :mandatory="false"
                                    >
                                        <v-radio label="1:1" :value="1"></v-radio>
                                        <v-radio label="3:2" :value="1.5"></v-radio>
                                        <v-radio label="16:9" :value="1.7777777777777777"></v-radio>
                                        <v-radio label="4:3" :value="1.3333333333333333"></v-radio>
                                    </v-radio-group>
                                </v-flex>
                                <v-flex xs12>
                                    <v-slider
                                        class="justify-center mr-3"
                                        v-model="wallpaperCropDegrees"
                                        label="Rotar"
                                        step="1"
                                        max="180"
                                        min="-180"
                                        thumb-label="always"
                                    ></v-slider>
                                </v-flex>
                                <v-btn
                                    color="primary"
                                    @click="cropWallpaper()"
                                >Recortar</v-btn>
                                <v-btn
                                    color="error"
                                    flat
                                    @click="dialogWallpaperCrop = false"
                                >Cancelar</v-btn>
                            </v-layout>
                        </v-card-actions>
                    </v-card>
                </v-dialog>

                <!-- ── Rulebook ── -->
                <v-flex xs12 sm6 md4 class="pa-2">
                    <v-card class="config-card">
                        <div class="config-card-header">
                            <v-icon class="config-card-icon">description</v-icon>
                            <span>Reglamento</span>
                        </div>
                        <v-card-text class="pt-3">
                            <!-- Current rulebook -->
                            <div v-if="rulebookUrl" class="rulebook-info mb-3">
                                <div class="rulebook-status">
                                    <v-icon small color="success">check_circle</v-icon>
                                    <span class="caption grey--text">Reglamento cargado</span>
                                </div>
                                <v-btn
                                    flat
                                    small
                                    color="primary"
                                    class="pl-0 mt-2"
                                    :href="rulebookUrl"
                                    target="_blank"
                                >
                                    <v-icon left small>file_download</v-icon>
                                    Descargar actual
                                </v-btn>
                            </div>
                            <div v-else class="rulebook-empty mb-3">
                                <v-icon size="48" color="grey lighten-2">description</v-icon>
                                <div class="caption grey--text mt-1">Sin reglamento</div>
                            </div>

                            <!-- File input -->
                            <v-btn flat color="primary" class="pl-0" @click="$refs.rulebookInput.click()">
                                <v-icon left>attach_file</v-icon>
                                {{ rulebookFile ? rulebookFile.name : 'Seleccionar documento' }}
                            </v-btn>
                            <input
                                ref="rulebookInput"
                                type="file"
                                accept=".pdf,.doc,.docx"
                                style="display: none;"
                                @change="onRulebookSelected"
                            />
                            <div v-if="rulebookError" class="caption error--text mt-1">{{ rulebookError }}</div>
                            <div v-if="rulebookFile" class="caption grey--text mt-1">
                                {{ (rulebookFile.size / 1024 / 1024).toFixed(2) }} MB
                            </div>
                        </v-card-text>
                        <div class="config-card-actions">
                            <v-btn
                                color="primary"
                                depressed
                                :loading="loadingRulebook"
                                :disabled="!rulebookFile"
                                @click="uploadRulebook()"
                            >
                                Guardar reglamento
                            </v-btn>
                        </div>
                    </v-card>
                </v-flex>

            </v-layout>
        </v-container>
    </div>
</template>

<script>
import '@/plugins/axios'
import store from '@/store'
import { mapGetters } from 'vuex'
import { USER_REQUEST } from '@/store/actions/user'
import VeeValidate, {Validator} from 'vee-validate'
import Vue from 'vue'
import VuejsClipper from 'vuejs-clipper'
import ClipperFixed from 'vuejs-clipper/src/components/clipper-fixed'

Vue.use(VeeValidate)
Vue.use(VuejsClipper, {
    components: {
        clipperFixed: 'clipper-fixed'
    }
})
    const dictionary = {
        en: {
            messages:{
                confirmed: () => 'The passwords didn\'t match',
                required: () => 'This field is required'
            }
        },
        es: {
            messages: {
                confirmed: () => 'Las contraseñas no coinciden',
                required: () => 'Este campo es requerido'
            }
        }
    };
    Validator.localize(dictionary);
    const validator = new Validator({ password_confirm: 'confirmed' });
    validator.localize('es');

    export default {
        name: "configuracion",
        components: { ClipperFixed },
        computed: {
            ...mapGetters(['getProfile']),
        },
        data () {
            return {
                current_password: '',
                password: '',
                confirm_password: '',
                show: false,
                errores: [],
                loadingNotification: false,
                loadingSurvey: false,
                loadingTolerance: false,
                loadingAccessDays: false,
                loadingChangePassword: false,
                loadingMaxiumCapacity: false,
                loadingWallpaper: false,
                canSMS: null,
                canEmail: null,
                health_survey: null,
                tolerance_week_invitations: null,
                Lunes: false,
                Martes: false,
                Miercoles: false,
                Jueves: false,
                Viernes: false,
                Sabado: false,
                Domingo: false,
                canDays: false,
                canSurvey: false,
                profileData: {},
                daysAccessPark: {},
                dialog_at_least_one_day: false,
                dias: '',
                maxium_capacity: null,
                wallpaperFile: null,
                wallpaperPreview: null,
                wallpaperError: null,
                dialogWallpaperCrop: false,
                wallpaperCropDegrees: 0,
                wallpaperCropRatio: 1.7777777777777777,
                wallpaperCropURL: null,
                wallpaperCroppedDataURL: null,
                rulebookFile: null,
                rulebookUrl: null,
                rulebookError: null,
                loadingRulebook: false,
            }
        },
        mounted() {
            this.getMaxiumCapacity()
            this.getRulebookUrl()
        },
        methods:{
            onWallpaperSelected(event) {
                const file = event.target.files[0];
                if (!file) return;
                if (!file.type.startsWith('image/')) {
                    this.wallpaperError = 'Solo se permiten archivos de imagen.';
                    return;
                }
                this.wallpaperError = null;
                this.wallpaperFile = file;
                const reader = new FileReader();
                reader.onload = (e) => { this.wallpaperPreview = e.target.result; };
                reader.readAsDataURL(file);
            },
            cropWallpaper() {
                const canvas = this.$refs.wallpaperClipper.clip();
                this.wallpaperCroppedDataURL = canvas.toDataURL('image/jpeg', 0.85);
                this.dialogWallpaperCrop = false;
            },
            uploadWallpaper() {
                if (!this.wallpaperFile && !this.wallpaperCroppedDataURL) return;
                this.loadingWallpaper = true;
                const formData = new FormData();

                if (this.wallpaperCroppedDataURL) {
                    // Convert dataURL to Blob and append
                    fetch(this.wallpaperCroppedDataURL)
                        .then(res => res.blob())
                        .then(blob => {
                            formData.append('wallpaper', blob, 'wallpaper.jpg');
                            this.sendWallpaperRequest(formData);
                        });
                } else {
                    // Use original file if no crop was done
                    formData.append('wallpaper', this.wallpaperFile);
                    this.sendWallpaperRequest(formData);
                }
            },
            sendWallpaperRequest(formData) {
                const config = {
                    method: 'PATCH',
                    url: 'https://empresa-service.intrare.app/empresas/wallpaper',
                    headers: {
                        'authorization': 'Token ' + store.getters.getToken
                    },
                    data: formData
                };
                axios(config)
                    .then(() => {
                        this.loadingWallpaper = false;
                        this.wallpaperFile = null;
                        this.wallpaperPreview = null;
                        this.wallpaperCroppedDataURL = null;
                        this.wallpaperCropDegrees = 0;
                        this.wallpaperCropRatio = 1.7777777777777777;
                        this.$store.dispatch(USER_REQUEST);
                        this.$store.commit('snackbar_activate_true');
                        this.$store.commit('snackbar_change_message', 'Fondo de pantalla actualizado');
                        setTimeout(this.clear, 3000);
                    })
                    .catch(error => {
                        this.loadingWallpaper = false;
                        this.wallpaperError = 'Error al subir la imagen. Inténtalo de nuevo.';
                        console.error(error);
                    });
            },
            change_password(){
                this.$validator.validateAll().then((result) => {
                    if (result) {
                        this.errores = []
                        this.loadingChangePassword = true
                        const config = {
                            method: 'POST',
                            url: 'https://empresa-service.intrare.app/auth/password',
                            headers: {
                                'Content-Type': 'application/json',
                                'Accept-Language': this.language ,
                                'authorization': 'Token ' + store.getters.getToken
                            },
                            data: {
                                new_password: this.password,
                                current_password: this.current_password
                            }
                        };
                        axios(config)
                            .then(() => {
                                this.loadingChangePassword = false
                                this.$store.commit('snackbar_activate_true')
                                this.$store.commit('snackbar_change_message', this.$t('msg_cambio_password'))
                                setTimeout(this.clear, 3000);
                            })
                            .catch(error => {
                                this.loadingChangePassword = false
                                if (error.response.status == 400
                                    && typeof error.response.data == "object"
                                    && error.response.data) {
                                    this.errores = error.response.data
                                }
                            })
                    }
                }).catch((e) => {
                    console.log(e);
                });
            },
            getProfileData () {
                const config = {
                    method: 'get',
                    url: 'https://empresa-service.intrare.app/auth/me',
                    headers: {
                        'Content-Type': 'application/json',
                        'authorization': 'Token ' + store.getters.getToken
                    }
                }
                axios(config)
                    .then(response => {
                        this.profileData = response.data
                        if (this.profileData.roll === 4){
                          this.canDays = true
                          this.getDaysAccessPark()
                        }
                        if (this.profileData.roll === 5) {
                           this.canSurvey = true
                        }
                        this.canSMS = this.profileData.can_sms
                        this.canEmail = this.profileData.can_email
                        this.health_survey = this.profileData.health_survey
                        this.tolerance_week_invitations = this.profileData.tolerance_week_invitations
                    })
                    .catch(error => {
                        console.log(error)
                    })
            },
            getDaysAccessPark () {
                if (this.profileData.roll === 4){
                    const config = {
                        method: 'get',
                        url: 'https://empresa-service.intrare.app/parques/getdiary',
                        headers: {
                            'Content-Type': 'application/json',
                            'authorization': 'Token ' + store.getters.getToken
                        }
                    }
                    axios(config)
                        .then(response => {
                            this.daysAccessPark = response.data
                            this.Lunes     = this.daysAccessPark.diary.includes('0')
                            this.Martes    = this.daysAccessPark.diary.includes('1')
                            this.Miercoles = this.daysAccessPark.diary.includes('2')
                            this.Jueves    = this.daysAccessPark.diary.includes('3')
                            this.Viernes   = this.daysAccessPark.diary.includes('4')
                            this.Sabado    = this.daysAccessPark.diary.includes('5')
                            this.Domingo   = this.daysAccessPark.diary.includes('6')
                        })
                        .catch(error => { console.log(error) })
                }
            },
            changeNotifications () {
                this.loadingNotification = true
                const config = {
                    method: 'PATCH',
                    url: 'https://empresa-service.intrare.app/UserPlatformUpdate/' + this.profileData.id + '/',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': this.language,
                        'authorization': 'Token ' + store.getters.getToken
                    },
                    data: { 'can_sms': this.canSMS, 'can_email': this.canEmail }
                }
                axios(config)
                    .then(() => {
                        this.loadingNotification = false
                        this.getProfileData()
                        this.$store.commit('snackbar_activate_true')
                        this.$store.commit('snackbar_change_message', this.$t('notificacion_success'))
                        setTimeout(this.clear, 3000)
                    })
                    .catch(error => {
                        this.loadingNotification = false
                        if (error.response.status === 400 && typeof error.response.data === 'object' && error.response.data) {
                            this.errores = error.response.data
                        }
                    })
            },
            changeSurvey () {
                this.loadingSurvey = true
                const config = {
                    method: 'PATCH',
                    url: 'https://empresa-service.intrare.app/empresas/health_survey',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': this.language,
                        'authorization': 'Token ' + store.getters.getToken
                    },
                    data: { 'health_survey': this.health_survey }
                }
                axios(config)
                    .then(() => {
                        this.loadingSurvey = false
                        this.getProfileData()
                        this.$store.commit('snackbar_activate_true')
                        this.$store.commit('snackbar_change_message', this.$t('notificacion_success'))
                        setTimeout(this.clear, 3000)
                    })
                    .catch(error => {
                        this.loadingSurvey = false
                        if (error.response.status === 400 && typeof error.response.data === 'object' && error.response.data) {
                            this.errores = error.response.data
                        }
                    })
            },
            enableToleranceOneWeek () {
                this.loadingTolerance = true
                const config = {
                    method: 'PATCH',
                    url: 'https://empresa-service.intrare.app/empresas/tolerance_week_invitations',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': this.language,
                        'authorization': 'Token ' + store.getters.getToken
                    },
                    data: { 'tolerance_week_invitations': this.tolerance_week_invitations }
                }
                axios(config)
                    .then(() => {
                        this.loadingTolerance = false
                        this.getProfileData()
                        this.$store.commit('snackbar_activate_true')
                        this.$store.commit('snackbar_change_message', this.$t('notificacion_success'))
                        setTimeout(this.clear, 3000)
                    })
                    .catch(error => {
                        this.loadingTolerance = false
                        if (error.response.status === 400 && typeof error.response.data === 'object' && error.response.data) {
                            this.errores = error.response.data
                        }
                    })
            },
            getMaxiumCapacity () {
                const config = {
                    method: 'GET',
                    url: 'https://empresa-service.intrare.app/empresas/my-empresa',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': this.language,
                        'authorization': 'Token ' + store.getters.getToken
                    }
                }
                axios(config)
                    .then(ok => {
                        this.maxium_capacity = ok.data.maxium_capacity
                    })
                    .catch(error => {
                        console.log(error)
                    })
            },
            changeMaxiumCapacity () {
                this.loadingMaxiumCapacity = true
                const config = {
                    method: 'PATCH',
                    url: 'https://empresa-service.intrare.app/empresas/maxium_capacity',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept-Language': this.language,
                        'authorization': 'Token ' + store.getters.getToken
                    },
                    data: { 'maxium_capacity': this.maxium_capacity }
                }
                axios(config)
                    .then(() => {
                        this.loadingMaxiumCapacity = false
                        this.getProfileData()
                        this.$store.commit('snackbar_activate_true')
                        this.$store.commit('snackbar_change_message', this.$t('notificacion_success'))
                        setTimeout(this.clear, 3000)
                    })
                    .catch(error => {
                        this.loadingMaxiumCapacity = false
                        if (error.response.status === 400 && typeof error.response.data === 'object' && error.response.data) {
                            this.errores = error.response.data
                        }
                    })
            },
            getDays(){
                this.dias = ''
                if(this.Lunes)     this.dias += '0'
                if(this.Martes)    this.dias += '1'
                if(this.Miercoles) this.dias += '2'
                if(this.Jueves)    this.dias += '3'
                if(this.Viernes)   this.dias += '4'
                if(this.Sabado)    this.dias += '5'
                if(this.Domingo)   this.dias += '6'
            },
            at_least_one_day(){
                return [this.Lunes, this.Martes, this.Miercoles, this.Jueves,
                        this.Viernes, this.Sabado, this.Domingo].filter(Boolean).length
            },
            changeAccessDays () {
                if(this.at_least_one_day() === 0) {
                    this.dialog_at_least_one_day = true
                } else {
                    this.loadingAccessDays = true
                    this.getDays()
                    const config = {
                        method: 'PATCH',
                        url: 'https://empresa-service.intrare.app/parques/' + this.daysAccessPark.id + '/update/',
                        headers: {
                            'Content-Type': 'application/json',
                            'Accept-Language': this.language,
                            'authorization': 'Token ' + store.getters.getToken
                        },
                        data: { diary: this.dias }
                    }
                    axios(config)
                        .then(() => {
                            this.loadingAccessDays = false
                            this.getDaysAccessPark()
                            this.$store.commit('snackbar_activate_true')
                            this.$store.commit('snackbar_change_message', this.$t('notificacion_success'))
                            setTimeout(this.clear, 3000)
                        })
                        .catch(error => {
                            this.loadingAccessDays = false
                            if (error.response.status === 400 && typeof error.response.data === 'object' && error.response.data) {
                                this.errores = error.response.data
                            }
                        })
                }
            },
            onRulebookSelected(event) {
                const file = event.target.files[0];
                if (!file) return;

                const allowedExtensions = ['pdf', 'doc', 'docx'];
                const fileName = file.name.toLowerCase();
                const fileExtension = fileName.split('.').pop();

                if (!allowedExtensions.includes(fileExtension)) {
                    this.rulebookError = 'Solo se permiten archivos PDF, DOC o DOCX.';
                    return;
                }

                const maxSizeMB = 5;
                if (file.size > maxSizeMB * 1024 * 1024) {
                    this.rulebookError = `El archivo no debe exceder ${maxSizeMB}MB.`;
                    return;
                }

                this.rulebookError = null;
                this.rulebookFile = file;
            },
            uploadRulebook() {
                if (!this.rulebookFile) return;
                this.loadingRulebook = true;
                const formData = new FormData();
                formData.append('file', this.rulebookFile);
                const config = {
                    method: 'POST',
                    url: 'https://empresa-service.intrare.app/empresas/upload_rulebook',
                    headers: {
                        'authorization': 'Token ' + store.getters.getToken
                    },
                    data: formData
                };
                axios(config)
                    .then(() => {
                        this.loadingRulebook = false;
                        this.rulebookFile = null;
                        this.getRulebookUrl();
                        this.$store.commit('snackbar_activate_true');
                        this.$store.commit('snackbar_change_message', 'Reglamento actualizado correctamente');
                        setTimeout(this.clear, 3000);
                    })
                    .catch(error => {
                        this.loadingRulebook = false;
                        let errorMessage = 'Error al subir el reglamento.';
                        if (error.response && error.response.data && error.response.data.error) {
                            errorMessage = error.response.data.error;
                        }
                        this.rulebookError = errorMessage;
                        console.error(error);
                    });
            },
            getRulebookUrl() {
                const config = {
                    method: 'GET',
                    url: 'https://empresa-service.intrare.app/empresas/my-empresa',
                    headers: {
                        'Content-Type': 'application/json',
                        'authorization': 'Token ' + store.getters.getToken
                    }
                };
                axios(config)
                    .then(response => {
                        this.rulebookUrl = response.data.rulebook;
                    })
                    .catch(error => {
                        console.error('Error al obtener reglamento:', error);
                    });
            },
            clear(){
                this.current_password = ''
                this.password = ''
                this.confirm_password = ''
                this.$validator.reset()
                this.errores = []
                this.$store.commit('snackbar_activate_false')
            }
        },
        beforeMount () {
            this.getProfileData()
        },
    }
</script>

<style scoped>
.config-page {
    padding: 16px;
}
.config-header {
    padding: 8px 8px 16px;
    border-bottom: 1px solid rgba(0,0,0,0.08);
    margin-bottom: 8px;
}
.config-title {
    font-size: 1.6rem;
    font-weight: 600;
    color: #212121;
    margin: 0 0 4px;
}
.config-subtitle {
    font-size: 0.875rem;
    color: #757575;
    margin: 0;
}
.config-card {
    border-radius: 8px;
    height: 100%;
    display: flex;
    flex-direction: column;
}
.config-card-header {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 14px 16px 0;
    font-size: 1rem;
    font-weight: 600;
    color: #212121;
    border-bottom: 2px solid var(--v-primary-base, #1976D2);
    padding-bottom: 12px;
    margin-bottom: 0;
}
.config-card-icon {
    color: var(--v-primary-base, #1976D2) !important;
}
.config-card-actions {
    padding: 8px 16px 16px;
    display: flex;
    justify-content: flex-end;
    margin-top: auto;
}

/* Wallpaper preview */
.wallpaper-preview-wrap {
    text-align: center;
}
.wallpaper-preview-img {
    width: 100%;
    max-height: 140px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid rgba(0,0,0,0.1);
}
.wallpaper-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
    border-radius: 6px;
    height: 100px;
}

/* Rulebook styles */
.rulebook-info {
    padding: 12px;
    background: #f5f5f5;
    border-radius: 6px;
    border-left: 4px solid var(--v-primary-base, #1976D2);
}
.rulebook-status {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 0.875rem;
}
.rulebook-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: #f5f5f5;
    border-radius: 6px;
    height: 100px;
}
</style>
